
import { useState, useEffect } from "react";
import {
  Briefcase,
  MapPin,
  DollarSign,
  Clock,
  CalendarDays,
  Bookmark,
  ExternalLink,
  Eye,
  CheckCircle2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "sonner";
import { SAVED_JOBS_API_END_POINT, APPLICATION_API_END_POINT } from "@/utils/constant";

function timeAgo(dateString) {
  if (!dateString) return "";
  const now = new Date();
  const posted = new Date(dateString);
  const diffMs = now - posted;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffDay > 30)
    return `${Math.floor(diffDay / 30)} month${Math.floor(diffDay / 30) > 1 ? "s" : ""} ago`;
  if (diffDay > 0) return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
  if (diffHour > 0) return `${diffHour} hour${diffHour > 1 ? "s" : ""} ago`;
  if (diffMin > 0) return `${diffMin} minute${diffMin > 1 ? "s" : ""} ago`;
  return "Just now";
}

// isApplied is passed from parent (Jobs.jsx) — fetched once, no per-card API call
function Job({ job, isApplied: isAppliedProp = false }) {
  const navigate = useNavigate();
  const u = useSelector((state) => state.auth?.user);
  const isAuthenticated = !!u;

  const [isSaved, setIsSaved]       = useState(false);
  const [isLoading, setIsLoading]   = useState(false);
  const [isApplied, setIsApplied]   = useState(isAppliedProp);
  const [isApplying, setIsApplying] = useState(false);

  // Sync if parent prop changes
  useEffect(() => {
    setIsApplied(isAppliedProp);
  }, [isAppliedProp]);

  const title       = job?.title?.S       || job?.title       || "Untitled";
  const location    = job?.location?.S    || job?.location    || "";
  const jobType     = job?.jobType?.S     || job?.jobType     || "";
  const salary      = job?.salary?.S      || job?.salary      || "";
  const experience  = job?.experience?.S  || job?.experience  || "";
  const description = job?.description?.S || job?.description || "";
  const createdAt   = job?.createdAt?.S   || job?.createdAt   || "";

  const companyName =
    job?.company?.CompanyName ||
    job?.company?.name ||
    job?.companyName?.S ||
    job?.companyName ||
    job?.company ||
    "Company";

  const companyLogo =
    job?.company?.logo ||
    job?.companyLogo?.S ||
    job?.companyLogo ||
    null;

  const jobId = job?._id || job?.jobId?.S || job?.jobId;

  const initials = companyName
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const shortDesc =
    description.length > 130 ? description.slice(0, 130) + "..." : description;

  // Check saved status on mount only (save check stays per-card, it's lightweight)
  useEffect(() => {
    if (!isAuthenticated || !jobId) return;
    const checkSaved = async () => {
      try {
        const res = await axios.get(
          `${SAVED_JOBS_API_END_POINT}/check/${jobId}`,
          { withCredentials: true }
        );
        setIsSaved(res.data.isSaved);
      } catch (err) {
        console.error("Error checking saved status:", err);
      }
    };
    checkSaved();
  }, [jobId, isAuthenticated]);

  const handleSave = async () => {
    if (!isAuthenticated) {
      toast.error("Please sign in to save jobs");
      navigate("/login");
      return;
    }
    try {
      setIsLoading(true);
      if (!isSaved) {
        await axios.post(
          `${SAVED_JOBS_API_END_POINT}/save`,
          { jobId },
          { withCredentials: true }
        );
        setIsSaved(true);
        toast.success("Job saved!");
      } else {
        await axios.delete(
          `${SAVED_JOBS_API_END_POINT}/unsave/${jobId}`,
          { withCredentials: true }
        );
        setIsSaved(false);
        toast.success("Job removed from saved");
      }
    } catch (err) {
      console.error("Error saving/unsaving job:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = async () => {
    if (!isAuthenticated) {
      toast.error("Please sign in to apply for jobs");
      navigate("/login");
      return;
    }
    if (isApplied) return;
    try {
      setIsApplying(true);
      const res = await axios.get(
        `${APPLICATION_API_END_POINT}/apply/${jobId}`,
        { withCredentials: true }
      );
      if (res.data.status) {
        setIsApplied(true);
        toast.success(res.data.message || "Applied successfully!");
      }
    } catch (err) {
      console.error("Error applying for job:", err);
      toast.error(err.response?.data?.message || "Already applied or something went wrong.");
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className="p-4 md:p-5 border border-gray-200 rounded-xl">

      {/* ── Top row: logo · title / company / location · time ago ── */}
      <div className="flex items-start gap-3">

        {/* Company logo / initials avatar */}
        <div className="flex-shrink-0 w-12 h-12 rounded-xl border border-gray-200 bg-[#EEF4FF] flex items-center justify-center overflow-hidden">
          {companyLogo ? (
            <img
              src={companyLogo}
              alt={companyName}
              className="w-full h-full object-contain p-1"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <span className="text-sm font-bold text-[#012760] select-none">{initials}</span>
          )}
        </div>

        {/* Title + company name + location */}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 mb-0.5 truncate">{companyName}</p>
          <h3 className="text-[15px] font-semibold text-[#012760] leading-snug line-clamp-1">{title}</h3>
          {location && (
            <div className="flex items-center gap-1 mt-1">
              <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <span className="text-xs text-gray-500 truncate">{location}</span>
            </div>
          )}
        </div>

        {/* Time ago — top right */}
        {createdAt && (
          <div className="flex-shrink-0 flex items-center gap-1 text-xs text-gray-400 whitespace-nowrap">
            <CalendarDays className="w-3.5 h-3.5" />
            <span>{timeAgo(createdAt)}</span>
          </div>
        )}
      </div>

      {/* ── Divider ── */}
      <div className="my-3 border-t border-gray-100" />

      {/* ── Info pills ── */}
      <div className="flex flex-wrap items-center gap-2">
        {experience && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5">
            <Briefcase className="w-3.5 h-3.5 text-[#012760]" />
            <span>{experience}</span>
          </div>
        )}
        {salary && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5">
            <DollarSign className="w-3.5 h-3.5 text-[#012760]" />
            <span>{salary}</span>
          </div>
        )}
        {jobType && (
          <div className="flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-md px-2.5 py-1.5">
            <Clock className="w-3.5 h-3.5 text-blue-600" />
            <span>{jobType}</span>
          </div>
        )}
      </div>

      {/* ── Description snippet ── */}
      {shortDesc && (
        <p className="mt-3 text-xs text-gray-500 leading-relaxed line-clamp-2">{shortDesc}</p>
      )}

      {/* ── Action buttons ── */}
      <div className="mt-3 flex items-center gap-2 flex-wrap">

        {/* Apply Now — correct state immediately from parent, zero flicker */}
        <button
          onClick={handleApply}
          disabled={isApplied || isApplying}
          className={`flex items-center gap-1.5 px-5 py-2 text-xs font-semibold rounded-lg transition-all duration-200
            ${isApplied
              ? "bg-green-50 border border-green-200 text-green-700 cursor-not-allowed"
              : "bg-[#012760] hover:bg-blue-900 text-white disabled:opacity-60 disabled:cursor-not-allowed"
            }`}
        >
          {isApplied ? (
            <><CheckCircle2 className="w-3.5 h-3.5" /><span>Applied</span></>
          ) : (
            <><ExternalLink className="w-3.5 h-3.5" /><span>{isApplying ? "Applying..." : "Apply Now"}</span></>
          )}
        </button>

        {/* Save Job */}
        <button
          onClick={handleSave}
          disabled={isLoading}
          className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg border transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed
            ${isSaved
              ? "bg-[#EEF4FF] border-[#012760] text-[#012760]"
              : "bg-white border-gray-200 text-gray-500 hover:border-[#012760] hover:text-[#012760]"
            }`}
        >
          <Bookmark
            className="w-3.5 h-3.5"
            fill={isSaved ? "#012760" : "none"}
            stroke={isSaved ? "#012760" : "currentColor"}
          />
          {isLoading ? "..." : isSaved ? "Saved" : "Save Job"}
        </button>

        {/* View Details */}
        <button
          onClick={() => navigate(`/description/${jobId}`)}
          className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg border border-gray-200 bg-white text-gray-600 hover:border-[#012760] hover:text-[#012760] transition-all duration-200"
        >
          <Eye className="w-3.5 h-3.5" />
          View Details
        </button>

      </div>
    </div>
  );
}

export default Job;
